import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@doccareplus/shared/lib/utils';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
}

/**
 * PageHeader — standard page title + subtitle + actions row.
 * Matches the `pageHeader(title, subtitle, actions)` helper in Medrecordly.dc.html.
 *
 * NO business logic — pure presentational.
 */
export function PageHeader({ title, subtitle, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-end gap-4 flex-wrap mb-[22px]',
        className
      )}
    >
      <div className="flex-1 min-w-[200px]">
        <h1 className="m-0 mb-1 text-[22px] font-semibold tracking-[-0.02em] text-[var(--text)]">
          {title}
        </h1>
        {subtitle && (
          <p className="m-0 text-[13.5px] text-[var(--text-2)]">{subtitle}</p>
        )}
      </div>
      {actions && <div>{actions}</div>}
    </div>
  );
}

/* ─── SectionHeader ────────────────────────────────────────── */
interface SectionHeaderProps {
  title: string;
  action?: ReactNode;
  className?: string;
}

/**
 * SectionHeader — card section title with optional action button.
 * Used inside Card components.
 */
export function SectionHeader({ title, action, className }: SectionHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-[18px] pt-[16px] pb-[4px]',
        className
      )}
    >
      <span className="text-[14.5px] font-medium text-[var(--text)]">{title}</span>
      {action && <div>{action}</div>}
    </div>
  );
}

/* ─── BackButton ───────────────────────────────────────────── */
interface BackButtonProps {
  label?: string;
  onClick?: () => void;
  to?: string;
  compact?: boolean;
  className?: string;
}

/**
 * BackButton — inline back navigation control.
 * Supports rendering as a click handler button or a React Router Link.
 * Includes a premium hover animation that shifts the chevron left.
 */
export function BackButton({ label, onClick, to, compact = false, className }: BackButtonProps) {
  const arrowIcon = (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="transition-transform duration-200 group-hover:-translate-x-[3px]"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );

  if (compact) {
    const compactContent = (
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="transition-transform duration-200 group-hover:-translate-x-[3px]"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    );

    const compactClasses = cn(
      'group w-[32px] h-[32px] flex items-center justify-center flex-none',
      'border border-[var(--border)] bg-[var(--surface)] rounded-[8px]',
      'text-[var(--text-2)] cursor-pointer hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
      'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ring)]',
      className
    );
    const compactLabel = label ?? 'Go back';

    if (to) {
      return (
        <Link to={to} className={compactClasses} aria-label={compactLabel}>
          {compactContent}
        </Link>
      );
    }

    return (
      <button
        type="button"
        onClick={onClick}
        className={compactClasses}
        aria-label={compactLabel}
      >
        {compactContent}
      </button>
    );
  }

  const commonClasses = cn(
    'group inline-flex items-center gap-[6px] mb-[14px]',
    'bg-transparent border-none text-[var(--text-2)] cursor-pointer',
    'text-[13px] font-semibold p-0 font-[inherit] hover:text-[var(--text)] transition-colors duration-200 focus:outline-none',
    className
  );

  if (to) {
    return (
      <Link to={to} className={commonClasses}>
        {arrowIcon}
        {label ?? 'Back'}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={commonClasses}>
      {arrowIcon}
      {label ?? 'Back'}
    </button>
  );
}
